import axios from 'axios';
import { getAllUsers } from './userService.ts';

const BASE_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
const WORKFLOW_API_URL = `${BASE_API_URL}/QmsMyTask`;

let userNameMapCache: Map<string, string> | null = null;

export interface ApprovalTask {
  id: string;
  approvalRecordId: number;
  documentId: string;
  documentNumber: string;
  documentTitle: string;
  documentCategory: string;
  currentVersion: string;
  submittedBy: string;
  submittedDate: string;
  daysWaiting?: number;
  priority: 'high' | 'medium' | 'low';
  currentStep: string;
  previousApprovers: string[];
  description: string;
  status: 'pending' | 'approved' | 'rejected';
  approvedDate?: string;
  approvalComment?: string;
  approvedBy?: string;
}

export interface ApprovalActionRequest {
  action: 'approve' | 'reject';
  comment?: string;
}

const pickFirst = (...values: any[]) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
  }
  return undefined;
};

const toDateString = (value: any) => {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return date.toLocaleString('en-US');
  }
  return String(value);
};

const getUserNameMap = async (): Promise<Map<string, string>> => {
  if (userNameMapCache) {
    return userNameMapCache;
  }

  try {
    const users = await getAllUsers();
    const map = new Map<string, string>();
    users.forEach((user: any) => {
      const id = user?.id ?? user?.Id ?? user?.userId ?? user?.UserId;
      const name = user?.userName ?? user?.username ?? user?.name ?? user?.Name;
      if (id !== undefined && id !== null && name) {
        map.set(String(id), String(name));
      }
    });
    userNameMapCache = map;
    return map;
  } catch (error) {
    console.warn('Failed to preload user map, submittedBy will fallback to raw value:', error);
    return new Map<string, string>();
  }
};

const resolveSubmittedBy = (rawSubmittedBy: any, raw: any, documentData: any, userNameMap: Map<string, string>) => {
  const submittedByUserId = pickFirst(
    raw?.submittedByUserId,
    raw?.SubmittedByUserId,
    raw?.submitterId,
    raw?.SubmitterId,
    raw?.createdByUserId,
    raw?.CreatedByUserId,
    raw?.userId,
    raw?.UserId,
    documentData?.createdByUserId,
    documentData?.CreatedByUserId
  );

  if (submittedByUserId !== undefined && submittedByUserId !== null && submittedByUserId !== '') {
    const foundById = userNameMap.get(String(submittedByUserId));
    if (foundById) return foundById;
  }

  const rawSubmittedByStr = rawSubmittedBy !== undefined && rawSubmittedBy !== null ? String(rawSubmittedBy) : '';
  if (rawSubmittedByStr && /^\d+$/.test(rawSubmittedByStr)) {
    const foundByNumeric = userNameMap.get(rawSubmittedByStr);
    if (foundByNumeric) return foundByNumeric;
  }

  return rawSubmittedByStr || '-';
};

const normalizeApprovalTask = (
  raw: any,
  fallbackStatus: ApprovalTask['status'],
  userNameMap: Map<string, string>
): ApprovalTask => {
  const documentData = raw?.document || raw?.Document || {};

  const submittedBy = pickFirst(
    raw?.submittedBy,
    raw?.SubmittedBy,
    raw?.createdBy,
    raw?.CreatedBy,
    raw?.submitter,
    raw?.Submitter,
    raw?.initiator,
    raw?.Initiator,
    raw?.applicant,
    raw?.Applicant,
    documentData?.createdBy,
    documentData?.CreatedBy
  );

  const submittedDateRaw = pickFirst(
    raw?.submittedDate,
    raw?.SubmittedDate,
    raw?.createdAt,
    raw?.CreatedAt,
    raw?.submissionTime,
    raw?.SubmissionTime,
    raw?.submittedAt,
    raw?.SubmittedAt,
    documentData?.createdAt,
    documentData?.CreatedAt
  );

  return {
    id: String(
      pickFirst(raw?.id, raw?.Id, raw?.approvalRecordId, raw?.ApprovalRecordId, raw?.taskId, raw?.TaskId) ||
      Math.random().toString(36).slice(2)
    ),
    approvalRecordId: Number(
      pickFirst(raw?.approvalRecordId, raw?.ApprovalRecordId, raw?.id, raw?.Id, 0)
    ),
    documentId: String(
      pickFirst(raw?.documentId, raw?.DocumentId, documentData?.id, documentData?.Id, '')
    ),
    documentNumber: String(
      pickFirst(raw?.documentNumber, raw?.DocumentNumber, documentData?.documentNumber, documentData?.DocumentNumber, '-')
    ),
    documentTitle: String(
      pickFirst(raw?.documentTitle, raw?.DocumentTitle, raw?.title, raw?.Title, documentData?.title, documentData?.Title, '-')
    ),
    documentCategory: String(
      pickFirst(raw?.documentCategory, raw?.DocumentCategory, raw?.category, raw?.Category, documentData?.category, documentData?.Category, '-')
    ),
    currentVersion: String(
      pickFirst(raw?.currentVersion, raw?.CurrentVersion, raw?.version, raw?.Version, documentData?.currentVersion, documentData?.CurrentVersion, '-')
    ),
    submittedBy: resolveSubmittedBy(submittedBy, raw, documentData, userNameMap),
    submittedDate: toDateString(submittedDateRaw),
    daysWaiting: Number(pickFirst(raw?.daysWaiting, raw?.DaysWaiting, raw?.waitingDays, raw?.WaitingDays, 0)),
    priority: String(pickFirst(raw?.priority, raw?.Priority, 'medium')).toLowerCase() as ApprovalTask['priority'],
    currentStep: String(pickFirst(raw?.currentStep, raw?.CurrentStep, raw?.stepName, raw?.StepName, '-')),
    previousApprovers: Array.isArray(pickFirst(raw?.previousApprovers, raw?.PreviousApprovers))
      ? pickFirst(raw?.previousApprovers, raw?.PreviousApprovers)
      : [],
    description: String(
      pickFirst(raw?.description, raw?.Description, raw?.documentDescription, raw?.DocumentDescription, documentData?.description, documentData?.Description, '-')
    ),
    status: String(pickFirst(raw?.status, raw?.Status, fallbackStatus)).toLowerCase() as ApprovalTask['status'],
    approvedDate: toDateString(pickFirst(raw?.approvedDate, raw?.ApprovedDate, raw?.actionDate, raw?.ActionDate)),
    approvalComment: String(pickFirst(raw?.approvalComment, raw?.ApprovalComment, raw?.comment, raw?.Comment, '') || ''),
    approvedBy: String(pickFirst(raw?.approvedBy, raw?.ApprovedBy, '') || ''),
  };
};

/**
 * Get pending approval tasks for current user
 * Calls the GET /api/QmsWorkflowInstance/pending?userId={userId} endpoint
 */
export const getPendingTasks = async (): Promise<ApprovalTask[]> => {
  try {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    
    if (!userId) {
      throw new Error('User ID not found');
    }
    
    const response = await axios.get(`${WORKFLOW_API_URL}/pending`, {
      params: { userId },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    const userNameMap = await getUserNameMap();
    const tasks = Array.isArray(response.data) ? response.data : [];
    return tasks.map((task) => normalizeApprovalTask(task, 'pending', userNameMap));
  } catch (error: any) {
    console.error('Failed to get pending tasks:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to get pending tasks'
    );
  }
};

/**
 * Get approved tasks history for current user
 * Calls the GET /api/QmsWorkflowInstance/approved?userId={userId} endpoint
 */
export const getApprovedTasks = async (): Promise<ApprovalTask[]> => {
  try {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    
    if (!userId) {
      throw new Error('User ID not found');
    }
    
    const response = await axios.get(`${WORKFLOW_API_URL}/approved`, {
      params: { userId },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    const userNameMap = await getUserNameMap();
    const tasks = Array.isArray(response.data) ? response.data : [];
    return tasks.map((task) => normalizeApprovalTask(task, 'approved', userNameMap));
  } catch (error: any) {
    console.error('Failed to get approved tasks:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to get approved tasks'
    );
  }
};

/**
 * Get rejected tasks history for current user
 * Calls the GET /api/QmsWorkflowInstance/rejected?userId={userId} endpoint
 */
export const getRejectedTasks = async (): Promise<ApprovalTask[]> => {
  try {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');
    
    if (!userId) {
      throw new Error('User ID not found');
    }
    
    const response = await axios.get(`${WORKFLOW_API_URL}/rejected`, {
      params: { userId },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    const userNameMap = await getUserNameMap();
    const tasks = Array.isArray(response.data) ? response.data : [];
    return tasks.map((task) => normalizeApprovalTask(task, 'rejected', userNameMap));
  } catch (error: any) {
    console.error('Failed to get rejected tasks:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to get rejected tasks'
    );
  }
};

/**
 * Approve a workflow task
 * Calls the POST /api/QmsMyTask/approve endpoint
 */
export const approveTask = async (approvalRecordId: string, comment?: string): Promise<any> => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await axios.post(
      `${WORKFLOW_API_URL}/approve`,
      {
        approvalRecordId: parseInt(approvalRecordId),
        approved: true,
        comment: comment || ''
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error: any) {
    console.error('Failed to approve task:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to approve task'
    );
  }
};

/**
 * Reject a workflow task
 * Calls the POST /api/QmsMyTask/reject endpoint with approved=false
 */
export const rejectTask = async (approvalRecordId: string, comment: string): Promise<any> => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await axios.post(
      `${WORKFLOW_API_URL}/reject`,
      {
        approvalRecordId: parseInt(approvalRecordId),
        approved: false,
        comment: comment
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error: any) {
    console.error('Failed to reject task:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to reject task'
    );
  }
};

/**
 * Get workflow instance details
 * Calls the GET /api/QmsWorkflowInstance/{instanceId} endpoint
 */
export const getWorkflowInstance = async (instanceId: string): Promise<any> => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await axios.get(`${WORKFLOW_API_URL}/${instanceId}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error: any) {
    console.error('Failed to get workflow instance:', error);
    throw new Error(
      error.response?.data?.message || 
      error.response?.data?.detail || 
      'Failed to get workflow instance'
    );
  }
};
